import * as RadixToast from '@radix-ui/react-toast';
import { cx, cva, VariantProps } from 'class-variance-authority';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { formatTxn } from './format-txn';

type TypeToast = {
  title: React.ReactNode | string;
  description?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  icon?: string;
  hash?: string;
  time?: string;
  href?: string;
};

const typeWrapperClasses = cva(['rounded-md '], {
  variants: {
    type: {
      default: 'text-white',
      success: 'border border-green-25',
      accent: 'text-green-25',
      info: 'text-white',
      warning: 'border border-amber-700',
      error: 'border border-amber-700',
    },
  },
  defaultVariants: {
    type: 'default',
  },
});

const typeClasses = cva(['rounded-md '], {
  variants: {
    type: {
      default: 'text-white',
      success: 'text-green-25',
      accent: 'text-green-25',
      info: 'text-white',
      warning: 'text-amber-700',
      error: 'text-error',
    },
  },
  defaultVariants: {
    type: 'default',
  },
});

const copyText = (text: string): void => {
  navigator.clipboard.writeText(text);
};

function Toast(
  props: TypeToast &
    VariantProps<typeof typeWrapperClasses> &
    VariantProps<typeof typeClasses> & {
      index: number;
      hash?: string;
      time?: string;
      icon?: string;
      href?: string;
    }
) {
  const { title, description, index, type, hash, time, icon, href } = props;

  return (
    <RadixToast.Root
      className={cx(
        'shadow-dropdown relative w-80 max-w-md gap-4 bg-backgroundPrimary py-2 pl-2 pr-8 text-white',
        typeWrapperClasses({ type }),
        icon ? 'grid grid-cols-[40px_minmax(0,1fr)]' : 'grid grid-cols-1'
      )}
      key={index}
    >
      <RadixToast.Close
        aria-label='Dismiss notification'
        className='absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-white'
        title='Dismiss notification'
      >
        <svg
          aria-hidden='true'
          className='h-3 w-3'
          viewBox='0 0 12 12'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M2.25 2.25L9.75 9.75M9.75 2.25L2.25 9.75'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
          />
        </svg>
      </RadixToast.Close>
      {icon && (
        <div
          className={cx(
            'm-1 flex h-full w-12 grow flex-col place-content-center items-center',
            typeClasses({ type })
          )}
        >
          <span className='material-symbols-outlined'> {icon}</span>
        </div>
      )}
      <div className='flex min-w-0 grow flex-col gap-1'>
        <RadixToast.Title className='break-words text-base font-normal text-white'>
          {title}
        </RadixToast.Title>
        {description && (
          <RadixToast.Description className='ToastDescription m-0 break-words text-sm leading-4 text-white'>
            {description}
          </RadixToast.Description>
        )}
        {hash && href && (
          <div className='flex flex-row items-center break-words pb-1 pt-1'>
            <div className='text-xs text-white'>
              <a href={href} target='_blank' rel='noreferrer noopener'>
                {formatTxn(hash)}
              </a>
            </div>
            {time && <div className='text-sm text-slate-600'>- {time} ago</div>}
          </div>
        )}
        {hash && !href && (
          <div
            className='line-clamp-2 w-14 cursor-pointer text-xs text-blue-400 hover:text-blue-200'
            onClick={() => copyText(hash)}
          >
            {formatTxn(hash)}{' '}
          </div>
        )}
      </div>
    </RadixToast.Root>
  );
}

const ToastContext = createContext<null | ((toast: TypeToast) => void)>(null);

export function useToast() {
  const toast = useContext(ToastContext);
  if (toast == null) throw 'No toast context';
  return toast;
}

export function ToastProvider({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [toasts, setToasts] = useState<TypeToast[]>([]);

  const addToast = useCallback((toast: TypeToast) => {
    setToasts((toasts) => [...toasts, toast]);
  }, []);

  return (
    <RadixToast.Provider>
      <ToastContext.Provider value={addToast}>{children}</ToastContext.Provider>
      {toasts.map((toast, index) => (
        <Toast key={index} {...toast} index={index} />
      ))}
      <RadixToast.Viewport
        className={cx(
          'fixed bottom-5 right-5 z-[100] m-0 flex w-96 max-w-fit list-none flex-col gap-2 outline-none',
          className
        )}
      />
    </RadixToast.Provider>
  );
}

interface EmojiProps {
  className?: string;
  label?: string;
  symbol: number;
}

export const Emoji = React.memo<EmojiProps>(
  ({
    className,
    label,
    symbol,
  }: {
    className?: string;
    label?: string;
    symbol: number;
  }) => (
    <span className={className} role='img' aria-label={label}>
      {String.fromCodePoint(symbol)}
    </span>
  )
);
